import { DestroyRef, Directive, ElementRef, OnInit, inject } from '@angular/core';

/**
 * Valor pegado con punto decimal: un solo punto con dígitos a ambos lados
 * (ej. "1.5", "12.25"). Longitud acotada para evitar backtracking costoso.
 * Cualquier otro formato se deja al manejo nativo de PrimeNG.
 */
const PEGADO_PUNTO_DECIMAL = /^\s*\d{1,15}\.\d{1,15}\s*$/;

/**
 * Hace que un `p-inputNumber` con `locale="es-CO"` trate el punto como coma decimal.
 *
 * Con `es-CO` el punto es el separador de miles: PrimeNG lo descarta al teclear y lo
 * elimina al pegar, de modo que "1.5" terminaba como 15 sin avisar al usuario.
 * La directiva intercepta únicamente ese caso y reinyecta el carácter equivalente
 * ("," o "1,5") por el flujo normal de PrimeNG, que sigue aplicando sus límites
 * (`min`, `max`, `maxFractionDigits`). No modifica el modelo ni toca el servidor:
 * la validación definitiva sigue siendo del backend.
 *
 * Uso: `<p-inputNumber appDecimalComa locale="es-CO" ... />`
 */
@Directive({
  selector: '[appDecimalComa]',
  standalone: true,
})
export class DecimalComaDirective implements OnInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // Captura: se ejecuta antes que los handlers del input interno de PrimeNG.
    this.host.addEventListener('keypress', this.onKeyPress, true);
    this.host.addEventListener('paste', this.onPaste, true);
    this.destroyRef.onDestroy(() => {
      this.host.removeEventListener('keypress', this.onKeyPress, true);
      this.host.removeEventListener('paste', this.onPaste, true);
    });
  }

  private readonly onKeyPress = (event: KeyboardEvent): void => {
    if (event.key !== '.' || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const input = this.inputEditable(event.target);
    if (!input) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    input.dispatchEvent(
      new KeyboardEvent('keypress', {
        key: ',',
        code: event.code,
        charCode: 44,
        keyCode: 44,
        which: 44,
        bubbles: true,
        cancelable: true,
      }),
    );
  };

  private readonly onPaste = (event: ClipboardEvent): void => {
    const input = this.inputEditable(event.target);
    const texto = event.clipboardData?.getData('text') ?? '';
    if (!input || !PEGADO_PUNTO_DECIMAL.test(texto)) {
      return;
    }
    const normalizado = texto.trim().replace('.', ',');
    const reemplazo = this.crearPegado(normalizado);
    if (!reemplazo) {
      return; // Navegador sin soporte: se conserva el comportamiento nativo.
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    input.dispatchEvent(reemplazo);
  };

  private inputEditable(target: EventTarget | null): HTMLInputElement | null {
    return target instanceof HTMLInputElement && !target.readOnly && !target.disabled
      ? target
      : null;
  }

  private crearPegado(texto: string): ClipboardEvent | null {
    try {
      const datos = new DataTransfer();
      datos.setData('text/plain', texto);
      const evento = new ClipboardEvent('paste', {
        clipboardData: datos,
        bubbles: true,
        cancelable: true,
      });
      return evento.clipboardData?.getData('text') === texto ? evento : null;
    } catch {
      return null;
    }
  }
}
